const GLOBAL_SYMBOLS_API_BASE = 'https://globalsymbols.com/api';

export interface GlobalSymbolsLicense {
  name: string | null;
  url: string | null;
  version: string | null;
  properties: string | null;
}

export interface GlobalSymbolsSymbolset {
  id: number;
  slug: string;
  name: string;
  publisher: string;
  publisher_url: string | null;
  status: string;
  licence: GlobalSymbolsLicense | null;
}

export interface GlobalSymbolsPicto {
  id: number;
  image_url: string;
  native_format: string;
  part_of_speech: string | null;
  symbolset: GlobalSymbolsSymbolset | null;
}

export interface GlobalSymbolsLabel {
  id: number;
  text: string;
  text_diacritised?: string | null;
  description?: string | null;
  language: string;
  picto: GlobalSymbolsPicto | null;
}

export async function searchGlobalSymbolsLabels(
  query: string,
  options?: {
    language?: string;
    limit?: number;
    symbolset?: string;
  }
): Promise<GlobalSymbolsLabel[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const params = new URLSearchParams();
  params.set('query', trimmedQuery);
  params.set('language', options?.language ?? 'eng');
  params.set('limit', String(options?.limit ?? 24));
  params.set('expand', 'picto.symbolset picto.symbolset.licence');
  if (options?.symbolset) {
    params.set('symbolset', options.symbolset);
  }

  const response = await fetch(`${GLOBAL_SYMBOLS_API_BASE}/v1/labels/search?${params.toString()}`);
  if (!response.ok) {
    const responseBody = await response.text().catch(() => '');
    throw new Error(
      `Global Symbols request failed (${response.status}). ${responseBody.slice(0, 200)}`
    );
  }

  const payload: unknown = await response.json();
  if (!Array.isArray(payload)) return [];
  return payload as GlobalSymbolsLabel[];
}

export function getGlobalSymbolImageUrl(label: GlobalSymbolsLabel): string {
  return typeof label.picto?.image_url === 'string' ? label.picto.image_url : '';
}

export function generateGlobalSymbolAltText(
  label: GlobalSymbolsLabel,
  searchTerm?: string
): string {
  const text = label.text?.trim();
  if (text) return text;
  const fallback = searchTerm?.trim();
  return fallback ? `Symbol for ${fallback}` : 'Symbol from Global Symbols';
}

export function buildGlobalSymbolCredit(label: GlobalSymbolsLabel): string {
  const setName = label.picto?.symbolset?.name?.trim();
  const licenseName = label.picto?.symbolset?.licence?.name?.trim();

  if (setName && licenseName) {
    return `${setName} (${licenseName}) via Global Symbols`;
  }
  if (setName) {
    return `${setName} via Global Symbols`;
  }
  return 'Symbol via Global Symbols';
}
